import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Shield, Plus, Trash2, Save, Loader2, Search } from 'lucide-react';
import { Heading } from '../../layout';
import { apiClient, API_BASE_URL } from '../../../services/apiClient';
import AdministrationBackLink from '../AdministrationBackLink';
import { useUIColors } from '../../../hooks/useTheme';
import {
  DERIVED_MODULE_CODES,
  SECTION_ACTIONS,
  SECTION_CATALOG,
  SECTION_TREE,
  derivedModuleCodesFromSections,
  isSectionPermissionCode,
  sectionActionCode,
} from '../../../utils/sectionCatalog';

const ACTION_KEYS = SECTION_ACTIONS.map((item) => item.key);

const RolesAndPermissionsWorkspace = ({ isDarkMode = false, showAdminBack = false }) => {
  const ui = useUIColors();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [selectedPermissionIds, setSelectedPermissionIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [roleForm, setRoleForm] = useState({ name: '', description: '' });
  const [creating, setCreating] = useState(false);
  const [query, setQuery] = useState('');

  const selectedRole = useMemo(
    () => roles.find((r) => Number(r.id) === Number(selectedRoleId)) || null,
    [roles, selectedRoleId]
  );

  const permissionByCode = useMemo(() => {
    const map = {};
    permissions.forEach((perm) => {
      map[perm.code] = perm;
    });
    return map;
  }, [permissions]);

  const selectedIdSet = useMemo(
    () => new Set(selectedPermissionIds.map((id) => Number(id))),
    [selectedPermissionIds]
  );

  const sectionCatalogReady = Boolean(permissionByCode[sectionActionCode(SECTION_CATALOG[0], 'read')]);

  const loadRoles = useCallback(async () => {
    const data = await apiClient(`${API_BASE_URL}/api/rbac/roles`);
    setRoles(Array.isArray(data) ? data : []);
    return Array.isArray(data) ? data : [];
  }, []);

  const loadPermissionsCatalog = useCallback(async () => {
    const data = await apiClient(`${API_BASE_URL}/api/rbac/permissions`);
    setPermissions(Array.isArray(data) ? data : []);
  }, []);

  const loadRolePermissions = useCallback(async (roleId) => {
    if (!roleId) {
      setSelectedPermissionIds([]);
      return;
    }
    const data = await apiClient(`${API_BASE_URL}/api/rbac/roles/${roleId}/permissions`);
    setSelectedPermissionIds(Array.isArray(data?.permission_ids) ? data.permission_ids : []);
    if (data?.role) {
      setRoleForm({
        name: data.role.name || '',
        description: data.role.description || '',
      });
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const [roleList] = await Promise.all([loadRoles(), loadPermissionsCatalog()]);
        if (cancelled) return;
        if (roleList.length) {
          const firstId = roleList[0].id;
          setSelectedRoleId(firstId);
          await loadRolePermissions(firstId);
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'No se pudo cargar roles y permisos.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadRoles, loadPermissionsCatalog, loadRolePermissions]);

  const selectRole = async (roleId) => {
    setSelectedRoleId(roleId);
    setStatusMessage('');
    setError('');
    try {
      await loadRolePermissions(roleId);
    } catch (err) {
      setError(err.message || 'No se pudieron cargar los permisos del rol.');
    }
  };

  const isCodeSelected = (code) => {
    const id = permissionByCode[code]?.id;
    return Boolean(id && selectedIdSet.has(Number(id)));
  };

  const patchCodes = (mutator) => {
    setSelectedPermissionIds((prev) => {
      const next = new Set(prev.map((id) => Number(id)));
      mutator(next);
      return Array.from(next);
    });
    setStatusMessage('');
  };

  const setSectionAction = (section, action, enabled) => {
    patchCodes((next) => {
      const apply = (act, on) => {
        const id = permissionByCode[sectionActionCode(section, act)]?.id;
        if (!id) return;
        if (on) next.add(Number(id));
        else next.delete(Number(id));
      };
      if (enabled) {
        apply('read', true);
        apply(action, true);
      } else if (action === 'read') {
        ACTION_KEYS.forEach((act) => apply(act, false));
      } else {
        apply(action, false);
      }
    });
  };

  const setSectionsActions = (sections, action, enabled) => {
    patchCodes((next) => {
      sections.forEach((section) => {
        const apply = (act, on) => {
          const id = permissionByCode[sectionActionCode(section, act)]?.id;
          if (!id) return;
          if (on) next.add(Number(id));
          else next.delete(Number(id));
        };
        if (enabled) {
          apply('read', true);
          apply(action, true);
        } else if (action === 'read') {
          ACTION_KEYS.forEach((act) => apply(act, false));
        } else {
          apply(action, false);
        }
      });
    });
  };

  const extraPermissionIds = useMemo(() => {
    const derivedCodes = new Set(DERIVED_MODULE_CODES);
    return selectedPermissionIds.filter((id) => {
      const perm = permissions.find((item) => Number(item.id) === Number(id));
      if (!perm) return true;
      if (isSectionPermissionCode(perm.code)) return false;
      if (derivedCodes.has(perm.code)) return false;
      return true;
    });
  }, [permissions, selectedPermissionIds]);

  const handleSavePermissions = async () => {
    if (!selectedRoleId) return;
    if (
      selectedRole?.code === 'administrador' &&
      !window.confirm(
        'Estás modificando los permisos del rol Administrador. Si quitas visibilidad de Administración, puedes perder acceso. ¿Continuar?'
      )
    ) {
      return;
    }
    setSaving(true);
    setError('');
    setStatusMessage('');
    try {
      const selectedSectionCodes = permissions
        .filter((perm) => isSectionPermissionCode(perm.code) && selectedIdSet.has(Number(perm.id)))
        .map((perm) => perm.code);
      const derivedCodes = derivedModuleCodesFromSections(selectedSectionCodes);
      const ids = new Set(
        permissions
          .filter((perm) => isSectionPermissionCode(perm.code) && selectedIdSet.has(Number(perm.id)))
          .map((perm) => Number(perm.id))
      );
      extraPermissionIds.forEach((id) => ids.add(Number(id)));
      derivedCodes.forEach((code) => {
        const id = permissionByCode[code]?.id;
        if (id) ids.add(Number(id));
      });
      await apiClient(`${API_BASE_URL}/api/rbac/roles/${selectedRoleId}/permissions`, {
        method: 'PUT',
        body: JSON.stringify({ permission_ids: Array.from(ids) }),
      });
      if (selectedRole && !selectedRole.is_system) {
        await apiClient(`${API_BASE_URL}/api/rbac/roles/${selectedRoleId}`, {
          method: 'PUT',
          body: JSON.stringify({
            name: roleForm.name.trim(),
            description: roleForm.description.trim(),
          }),
        });
        await loadRoles();
      }
      await loadRolePermissions(selectedRoleId);
      setStatusMessage('Cambios guardados. Los usuarios del rol verán los cambios al volver a entrar.');
    } catch (err) {
      setError(err.message || 'No se pudo guardar.');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateRole = async () => {
    const name = roleForm.name.trim();
    if (!name) {
      setError('Indica un nombre para el nuevo rol.');
      return;
    }
    setCreating(true);
    setError('');
    try {
      const created = await apiClient(`${API_BASE_URL}/api/rbac/roles`, {
        method: 'POST',
        body: JSON.stringify({
          name,
          description: roleForm.description.trim(),
        }),
      });
      const roleList = await loadRoles();
      const newId = created?.id || roleList.find((r) => r.name === name)?.id;
      if (newId) {
        setSelectedRoleId(newId);
        await loadRolePermissions(newId);
      }
      setStatusMessage('Rol creado. Marca las secciones visibles y guarda.');
    } catch (err) {
      setError(err.message || 'No se pudo crear el rol.');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteRole = async () => {
    if (!selectedRole || selectedRole.is_system) return;
    if (!window.confirm(`¿Eliminar el rol "${selectedRole.name}"?`)) return;
    setError('');
    try {
      await apiClient(`${API_BASE_URL}/api/rbac/roles/${selectedRole.id}`, {
        method: 'DELETE',
      });
      const roleList = await loadRoles();
      if (roleList.length) {
        setSelectedRoleId(roleList[0].id);
        await loadRolePermissions(roleList[0].id);
      } else {
        setSelectedRoleId(null);
        setSelectedPermissionIds([]);
      }
      setStatusMessage('Rol eliminado.');
    } catch (err) {
      setError(err.message || 'No se pudo eliminar el rol.');
    }
  };

  const startCreateMode = () => {
    setSelectedRoleId(null);
    setSelectedPermissionIds([]);
    setRoleForm({ name: '', description: '' });
    setStatusMessage('');
    setError('');
  };

  const normalizedQuery = query.trim().toLowerCase();
  const visibleTree = useMemo(() => {
    if (!normalizedQuery) return SECTION_TREE;
    return SECTION_TREE.map((unit) => ({
      ...unit,
      groups: unit.groups
        .map((group) => ({
          ...group,
          items: group.items.filter((item) => {
            const haystack = `${unit.unit} ${group.title} ${item.label} ${item.href}`.toLowerCase();
            return haystack.includes(normalizedQuery);
          }),
        }))
        .filter((group) => group.items.length),
    })).filter((unit) => unit.groups.length);
  }, [normalizedQuery]);

  const panelStyle = {
    border: `1px solid ${ui.border}`,
    borderRadius: 12,
    background: ui.panel,
    minHeight: 420,
  };

  const headerCell = {
    fontSize: 11,
    fontWeight: 700,
    color: ui.muted,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    padding: '8px 6px',
    textAlign: 'center',
  };

  const renderSectionRow = (section) => {
    const visible = isCodeSelected(sectionActionCode(section, 'read'));
    return (
      <tr key={section.id || `${section.unitKey}.${section.key}`}>
        <td style={{ padding: '8px 10px', color: ui.text, fontSize: 13 }}>
          {section.label}
          <div style={{ fontSize: 11, color: ui.muted }}>{section.href}</div>
        </td>
        {SECTION_ACTIONS.map((action) => {
          const checked = isCodeSelected(sectionActionCode(section, action.key));
          const disabled = !permissionByCode[sectionActionCode(section, action.key)];
          return (
            <td key={action.key} style={{ textAlign: 'center', padding: '6px' }}>
              <input
                type="checkbox"
                checked={checked}
                disabled={disabled || (action.key !== 'read' && !visible && !checked)}
                onChange={(event) => setSectionAction(section, action.key, event.target.checked)}
                aria-label={`${section.label}: ${action.label}`}
              />
            </td>
          );
        })}
      </tr>
    );
  };

  const renderGroupHeader = (unit, group) => {
    const sections = group.items.map((item) => ({
      ...item,
      unit: unit.unit,
      unitKey: unit.unitKey,
      groupTitle: group.title,
    }));
    return (
      <tr key={`${unit.unitKey}-${group.key}-head`}>
        <td
          colSpan={1}
          style={{
            padding: '10px 10px 6px',
            fontSize: 12,
            fontWeight: 700,
            color: ui.text,
            background: `color-mix(in srgb, ${ui.panel} 88%, ${ui.border})`,
          }}
        >
          {group.title}
        </td>
        {SECTION_ACTIONS.map((action) => {
          const allOn = sections.every((section) => isCodeSelected(sectionActionCode(section, action.key)));
          const someOn =
            !allOn && sections.some((section) => isCodeSelected(sectionActionCode(section, action.key)));
          return (
            <td
              key={action.key}
              style={{
                textAlign: 'center',
                background: `color-mix(in srgb, ${ui.panel} 88%, ${ui.border})`,
              }}
            >
              <input
                type="checkbox"
                checked={allOn}
                ref={(el) => {
                  if (el) el.indeterminate = someOn;
                }}
                onChange={(event) => setSectionsActions(sections, action.key, event.target.checked)}
                aria-label={`${group.title}: ${action.label}`}
              />
            </td>
          );
        })}
      </tr>
    );
  };

  return (
    <div>
      <Heading level={2} isDarkMode={isDarkMode}>
        <Shield size={20} style={{ marginRight: 8, verticalAlign: 'middle' }} />
        Roles y permisos
      </Heading>
      {showAdminBack ? <AdministrationBackLink /> : null}
      <p style={{ color: ui.muted, fontSize: 13, marginTop: 8, marginBottom: 16 }}>
        Elige qué secciones ve cada rol y, en cada ventana, si puede ver, crear, editar o borrar.
        Administración sigue reservada al rol Administrador.
      </p>

      {loading ? (
        <div style={{ display: 'grid', placeItems: 'center', minHeight: 200, color: ui.muted }}>
          <Loader2 size={22} />
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(220px, 280px) 1fr',
            gap: 16,
            alignItems: 'start',
          }}
        >
          <div style={{ ...panelStyle, padding: 12 }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 10,
              }}
            >
              <strong style={{ color: ui.text, fontSize: 14 }}>Roles</strong>
              <button
                type="button"
                onClick={startCreateMode}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  border: `1px solid ${ui.border}`,
                  background: ui.panel,
                  color: ui.text,
                  borderRadius: 8,
                  padding: '4px 8px',
                  fontSize: 12,
                  cursor: 'pointer',
                }}
              >
                <Plus size={14} /> Nuevo
              </button>
            </div>
            <div style={{ display: 'grid', gap: 6 }}>
              {roles.map((role) => {
                const active = Number(role.id) === Number(selectedRoleId);
                return (
                  <button
                    key={role.id}
                    type="button"
                    onClick={() => selectRole(role.id)}
                    style={{
                      textAlign: 'left',
                      border: `1px solid ${active ? ui.interactive : ui.border}`,
                      background: active
                        ? `color-mix(in srgb, ${ui.interactive} 12%, ${ui.panel})`
                        : ui.panel,
                      color: ui.text,
                      borderRadius: 8,
                      padding: '10px 10px',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ fontWeight: 650, fontSize: 13 }}>{role.name}</div>
                    <div style={{ fontSize: 11, color: ui.muted, marginTop: 2 }}>
                      {role.code}
                      {role.is_system ? ' · sistema' : ''}
                      {typeof role.users_count === 'number' ? ` · ${role.users_count} usuarios` : ''}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ ...panelStyle, padding: 16 }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 12,
                flexWrap: 'wrap',
                marginBottom: 14,
              }}
            >
              <div style={{ flex: 1, minWidth: 220 }}>
                <label style={{ display: 'block', fontSize: 12, color: ui.muted, marginBottom: 4 }}>
                  Nombre del rol
                </label>
                <input
                  value={roleForm.name}
                  onChange={(e) => setRoleForm((prev) => ({ ...prev, name: e.target.value }))}
                  disabled={Boolean(selectedRole?.is_system)}
                  style={{
                    width: '100%',
                    height: 36,
                    borderRadius: 8,
                    border: `1px solid ${ui.border}`,
                    background: ui.panel,
                    color: ui.text,
                    padding: '0 10px',
                    boxSizing: 'border-box',
                  }}
                  placeholder={selectedRole ? selectedRole.name : 'Nuevo rol'}
                />
                <label
                  style={{ display: 'block', fontSize: 12, color: ui.muted, margin: '10px 0 4px' }}
                >
                  Descripción
                </label>
                <input
                  value={roleForm.description}
                  onChange={(e) =>
                    setRoleForm((prev) => ({ ...prev, description: e.target.value }))
                  }
                  disabled={Boolean(selectedRole?.is_system)}
                  style={{
                    width: '100%',
                    height: 36,
                    borderRadius: 8,
                    border: `1px solid ${ui.border}`,
                    background: ui.panel,
                    color: ui.text,
                    padding: '0 10px',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                {!selectedRoleId ? (
                  <button
                    type="button"
                    onClick={handleCreateRole}
                    disabled={creating}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      height: 36,
                      padding: '0 12px',
                      borderRadius: 8,
                      border: 'none',
                      background: ui.interactive,
                      color: '#fff',
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: 13,
                    }}
                  >
                    {creating ? <Loader2 size={14} /> : <Plus size={14} />}
                    Crear rol
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={handleSavePermissions}
                      disabled={saving}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        height: 36,
                        padding: '0 12px',
                        borderRadius: 8,
                        border: 'none',
                        background: ui.interactive,
                        color: '#fff',
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: 13,
                      }}
                    >
                      {saving ? <Loader2 size={14} /> : <Save size={14} />}
                      Guardar
                    </button>
                    {!selectedRole?.is_system ? (
                      <button
                        type="button"
                        onClick={handleDeleteRole}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          height: 36,
                          padding: '0 12px',
                          borderRadius: 8,
                          border: `1px solid ${ui.border}`,
                          background: ui.panel,
                          color: '#b91c1c',
                          cursor: 'pointer',
                          fontSize: 13,
                        }}
                      >
                        <Trash2 size={14} />
                        Eliminar
                      </button>
                    ) : null}
                  </>
                )}
              </div>
            </div>

            {error ? (
              <div style={{ color: '#b91c1c', fontSize: 13, marginBottom: 10 }}>{error}</div>
            ) : null}
            {statusMessage ? (
              <div style={{ color: '#047857', fontSize: 13, marginBottom: 10 }}>{statusMessage}</div>
            ) : null}

            {!selectedRoleId ? (
              <div style={{ color: ui.muted, fontSize: 13 }}>
                Crea un rol o selecciona uno de la lista para editar sus permisos.
              </div>
            ) : (
              <div>
                {!sectionCatalogReady ? (
                  <div
                    style={{
                      color: '#92400e',
                      fontSize: 13,
                      marginBottom: 12,
                      padding: 10,
                      borderRadius: 8,
                      background: 'rgba(245, 158, 11, 0.12)',
                    }}
                  >
                    Faltan los permisos por sección en la base de datos. Corre las migraciones y recarga
                    para poder marcar visibilidad y acciones.
                  </div>
                ) : null}
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 12,
                    maxWidth: 360,
                    position: 'relative',
                  }}
                >
                  <Search size={14} color={ui.muted} style={{ position: 'absolute', left: 10 }} />
                  <input
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Buscar sección…"
                    style={{
                      width: '100%',
                      height: 34,
                      borderRadius: 8,
                      border: `1px solid ${ui.border}`,
                      background: ui.panel,
                      color: ui.text,
                      padding: '0 10px 0 30px',
                      boxSizing: 'border-box',
                      fontSize: 13,
                    }}
                  />
                </label>
                <div style={{ overflow: 'auto', maxHeight: '70vh' }}>
                  {visibleTree.map((unit) => (
                    <div key={unit.unitKey} style={{ marginBottom: 18 }}>
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: 750,
                          color: ui.text,
                          margin: '4px 0 8px',
                        }}
                      >
                        {unit.unit}
                      </div>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr>
                            <th style={{ ...headerCell, textAlign: 'left' }}>Sección</th>
                            {SECTION_ACTIONS.map((action) => (
                              <th key={action.key} style={{ ...headerCell, minWidth: 72 }}>
                                {action.label}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {unit.groups.map((group) => (
                            <React.Fragment key={`${unit.unitKey}-${group.key}`}>
                              {renderGroupHeader(unit, group)}
                              {group.items.map((item) =>
                                renderSectionRow({
                                  ...item,
                                  unit: unit.unit,
                                  unitKey: unit.unitKey,
                                  groupTitle: group.title,
                                  id: `${unit.unitKey}.${item.key}`,
                                })
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RolesAndPermissionsWorkspace;
